import { FLAG_RC_ANALYTICS_IFRAME } from '@app/feature-flags/flags';
import { useFlags } from '@app/hooks/use-flags';
import React, { useEffect } from 'react';

import { TabGroup } from '../../components/tab-group/tab-group';
import { FLAG_ENABLE_CH_DASHBOARD_ANALYTICS } from '../../constants';
import { useAnalyticsContext } from '../../providers/analytics-provider';
import { getAnalyticsTabs, resolveActiveTab } from './get-analytics-tabs';

type Props = {
  setSelectedIndex: (selected: number, tabName: string) => void;
};

export const TabGroupWrapper = ({ setSelectedIndex }: Props) => {
  const flags = useFlags();
  const {
    state: { selectedTab },
    dispatch,
  } = useAnalyticsContext();

  const enableCHDashboardAnalytics =
    flags?.[FLAG_ENABLE_CH_DASHBOARD_ANALYTICS];
  const showAdminPanel = flags?.[FLAG_RC_ANALYTICS_IFRAME];

  const tabs = getAnalyticsTabs({ enableCHDashboardAnalytics, showAdminPanel });
  const { activeIndex, activeTab } = resolveActiveTab(tabs, selectedTab);

  // When a persisted selectedTab is no longer valid (e.g. feature flag toggled
  // off), heal the state so the cookie and toolbar stay in sync with what is
  // actually rendered. Guard on flags being defined so we don't clobber a
  // valid flag-gated tab (e.g. 'admin') while LaunchDarkly is still loading —
  // the tab list is incomplete until flags resolve. Dispatch directly — not
  // via setSelectedIndex — to avoid firing analytics for a state repair.
  useEffect(() => {
    if (flags === undefined) return;
    if (selectedTab !== activeTab) {
      dispatch({
        type: 'analytics tab updated',
        payload: { index: activeIndex, tabName: activeTab },
      });
    }
  }, [flags, selectedTab, activeTab, activeIndex, dispatch]);

  const handleTabChange = (index: number) => {
    const tab = tabs[index];
    setSelectedIndex(index, tab.name);
  };

  return (
    <TabGroup
      tabs={tabs}
      localePrefix="analytics"
      selectedIndex={activeIndex}
      setSelectedIndex={handleTabChange}
    />
  );
};
